import { useState, useEffect } from 'react';
import { TestTube, Play, CheckCircle, X, Clock, Bug, Target, Zap, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { settingsService, TestSuite, TestEnvironment, TestSettings, QualityMetrics } from '../../services/settingsService';
import { toast } from 'react-hot-toast';

const QATestingTab = () => {
  const [testSuites, setTestSuites] = useState<TestSuite[]>([]);
  const [testEnvironments, setTestEnvironments] = useState<TestEnvironment[]>([]);
  const [testSettings, setTestSettings] = useState<TestSettings>({
    autoRunOnCommit: true,
    parallelExecution: true,
    failFast: false,
    bypassEnvironmentValidation: false,
    generateReports: true,
    slackNotifications: true,
    retryFailedTests: 2
  });
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetrics>({
    testCoverage: 0,
    codeQuality: 'N/A',
    avgTestTime: '0s'
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadQATestingData();
  }, []);

  const loadQATestingData = async () => {
    try {
      setLoading(true);
      const qaData = await settingsService.loadQATestingData();
      setTestSuites(Array.isArray(qaData.testSuites) ? qaData.testSuites : []);
      setTestEnvironments(Array.isArray(qaData.testEnvironments) ? qaData.testEnvironments : []);
      setTestSettings(qaData.testSettings || {
        autoRunOnCommit: true,
        parallelExecution: true,
        failFast: false,
        bypassEnvironmentValidation: false,
        generateReports: true,
        slackNotifications: true,
        retryFailedTests: 2
      });
      setQualityMetrics(qaData.qualityMetrics || {
        testCoverage: 0,
        codeQuality: 'N/A',
        avgTestTime: '0s'
      });
    } catch (error) {
      console.error('Error loading QA testing data:', error);
      toast.error('Failed to load QA testing data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'running':
        return <Clock size={16} className="text-yellow-400" />;
      case 'failed':
        return <X size={16} className="text-red-400" />;
      default:
        return <TestTube size={16} className="text-slate-400" />;
    }
  };

  const handleSettingChange = async (key: string, value: boolean | number) => {
    try {
      const updatedSettings = { ...testSettings, [key]: value };
      setTestSettings(updatedSettings);
      
      await settingsService.updateQATestingSettings({ [key]: value });
      toast.success('QA testing settings updated');
    } catch (error) {
      console.error('Error updating QA testing settings:', error);
      toast.error('Failed to update QA testing settings');
      setTestSettings(testSettings);
    }
  };

  const runTestSuite = async (suiteId: string) => {
    setActionLoading(prev => ({ ...prev, [suiteId]: true }));
    try {
      const result = await settingsService.runTestSuite(suiteId);
      toast.success(result.message);
      await loadQATestingData();
    } catch (error) {
      console.error('Error running test suite:', error);
      toast.error('Failed to run test suite');
    } finally {
      setActionLoading(prev => ({ ...prev, [suiteId]: false }));
    }
  };

  const runAllTestSuites = async () => {
    setActionLoading(prev => ({ ...prev, 'all': true }));
    try {
      const result = await settingsService.runAllTestSuites();
      toast.success(result.message);
      await loadQATestingData();
    } catch (error) {
      console.error('Error running all test suites:', error);
      toast.error('Failed to run all test suites');
    } finally {
      setActionLoading(prev => ({ ...prev, 'all': false }));
    }
  };

  const performHealthCheck = async (environmentId: string) => {
    setActionLoading(prev => ({ ...prev, [environmentId]: true }));
    try {
      const result = await settingsService.performHealthCheck(environmentId);
      toast.success(`Health check completed: ${result.status}`);
      await loadQATestingData();
    } catch (error) {
      console.error('Error performing health check:', error);
      toast.error('Failed to perform health check');
    } finally {
      setActionLoading(prev => ({ ...prev, [environmentId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">QA Testing</h2>
          <p className="text-slate-400 mt-1">Manage test suites, environments, and quality assurance</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">QA Testing</h2>
        <p className="text-slate-400 mt-1">Manage test suites, environments, and quality assurance</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TestTube size={20} className="text-brand-accent" />
              <span>Test Suites</span>
            </div>
            <Button 
              size="sm" 
              onClick={runAllTestSuites}
              disabled={actionLoading['all']}
            >
              {actionLoading['all'] ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Play size={16} className="mr-2" />
              )}
              Run All Tests
            </Button>
          </CardTitle>
          <CardDescription>Monitor and execute your automated test suites</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(testSuites || []).map((suite) => (
              <div key={suite.id} className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg border border-slate-700/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-brand-primary/20 rounded-lg">
                      {getStatusIcon(suite.status)}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow">{suite.name}</h4>
                      <p className="text-xs text-slate-400">{suite.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      suite.status === 'passed' ? 'bg-green-400/20 text-green-400' :
                      suite.status === 'running' ? 'bg-yellow-400/20 text-yellow-400' :
                      suite.status === 'failed' ? 'bg-red-400/20 text-red-400' :
                      'bg-slate-400/20 text-slate-400'
                    }`}>
                      {suite.status}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => runTestSuite(suite.id)}
                      disabled={actionLoading[suite.id]}
                    >
                      {actionLoading[suite.id] ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-brand-primary"></div>
                      ) : (
                        <Play size={14} />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4 mb-3">
                  <div className="text-center p-2 bg-slate-800/50 rounded">
                    <div className="text-lg font-semibold text-brand-text">{suite.tests?.total || 0}</div>
                    <div className="text-xs text-slate-400">Total</div>
                  </div>
                  <div className="text-center p-2 bg-green-500/10 rounded">
                    <div className="text-lg font-semibold text-green-400">{suite.tests?.passed || 0}</div>
                    <div className="text-xs text-slate-400">Passed</div>
                  </div>
                  <div className="text-center p-2 bg-red-500/10 rounded">
                    <div className="text-lg font-semibold text-red-400">{suite.tests?.failed || 0}</div>
                    <div className="text-xs text-slate-400">Failed</div>
                  </div>
                  <div className="text-center p-2 bg-yellow-500/10 rounded">
                    <div className="text-lg font-semibold text-yellow-400">{suite.tests?.skipped || 0}</div>
                    <div className="text-xs text-slate-400">Skipped</div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Last run: {suite.lastRun} • Duration: {suite.duration}</span>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm">View Report</Button>
                    <Button variant="ghost" size="sm">View Logs</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target size={20} className="text-brand-accent" />
            <span>Test Environments</span>
          </CardTitle>
          <CardDescription>Configure and manage testing environments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(testEnvironments || []).map((env) => (
              <div key={env.id} className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-brand-primary/20 rounded-lg">
                    <Target size={16} className="text-brand-accent" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-brand-text">{env.name}</div>
                    <div className="text-xs text-slate-400">{env.url}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      env.status === 'active' ? 'bg-green-400/20 text-green-400' : 'bg-slate-400/20 text-slate-400'
                    }`}>
                      {env.status}
                    </span>
                    <div className="text-xs text-slate-400 mt-1">Last test: {env.lastTest}</div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => performHealthCheck(env.id)}
                    disabled={actionLoading[env.id]}
                  >
                    {actionLoading[env.id] ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-brand-primary mr-2"></div>
                    ) : null}
                    Test Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Zap size={20} className="text-brand-accent" />
            <span>Test Configuration</span>
          </CardTitle>
          <CardDescription>Configure test execution and reporting settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Play size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Auto-run on commit</div>
                <div className="text-xs text-slate-400">Automatically run tests when code is committed</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testSettings?.autoRunOnCommit || false}
                onChange={(e) => handleSettingChange('autoRunOnCommit', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Zap size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Parallel execution</div>
                <div className="text-xs text-slate-400">Run tests in parallel for faster execution</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testSettings?.parallelExecution || false}
                onChange={(e) => handleSettingChange('parallelExecution', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <X size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Fail fast</div>
                <div className="text-xs text-slate-400">Stop execution on first test failure</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testSettings?.failFast || false}
                onChange={(e) => handleSettingChange('failFast', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Bug size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Generate reports</div>
                <div className="text-xs text-slate-400">Create detailed test reports and coverage</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testSettings?.generateReports || false}
                onChange={(e) => handleSettingChange('generateReports', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Shield size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Bypass environment validation</div>
                <div className="text-xs text-slate-400">Skip environment checks for development and testing</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testSettings?.bypassEnvironmentValidation || false}
                onChange={(e) => handleSettingChange('bypassEnvironmentValidation', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Retry failed tests</label>
            <Input
              type="number"
              value={testSettings?.retryFailedTests || 0}
              onChange={(e) => handleSettingChange('retryFailedTests', parseInt(e.target.value))}
              className="violett-input"
              min="0"
              max="5"
            />
            <p className="text-xs text-slate-400 mt-1">Number of times to retry failed tests</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bug size={20} className="text-brand-accent" />
            <span>Quality Metrics</span>
          </CardTitle>
          <CardDescription>Track code quality and test coverage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-green-400">{qualityMetrics?.testCoverage || 0}%</div>
              <div className="text-sm text-slate-400">Test Coverage</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-blue-400">{qualityMetrics?.codeQuality || 'N/A'}</div>
              <div className="text-sm text-slate-400">Code Quality</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-purple-400">{qualityMetrics?.avgTestTime || '0s'}</div>
              <div className="text-sm text-slate-400">Avg Test Time</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QATestingTab;
